import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';

import { selectIsLogged } from '../../../store/auth/authReducer';
import { RootStackParamList } from '../../navigator';
import { DesktopBottomBars } from './DesktopBottomBars';
import { DesktopCoverflow, getDesktopCoverflowNavHeight } from './DesktopCoverflow';
import { useDesktopLayout } from './desktopLayout';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// The desktop page for the screens the mobile redesign browses on a swipe wheel - the lobby
// rooms, a room's tables, the VIP tables, the tournaments. None of them has a desktop frame in
// Figma, so they are laid out in the desktop home's terms: the same 592-high block centred
// between the top bar and the dock, the cards on a coverflow across the full content width
// (DesktopCoverflow), and a bottom row at the home's own 510 - by default the home's
// recent-tables / jackpot pair, so the pair stays put moving between the home and these screens.
const BLOCK_HEIGHT = 592;
/** The tallest the front card gets. */
export const DESKTOP_STRIP_CARD_HEIGHT = 440;
/** The bottom row ends where the home's does, 592 down, and is 82 high unless told otherwise. */
const FOOTER_HEIGHT = BLOCK_HEIGHT - 510;
const FOOTER_GAP = 18;

type Props<T> = {
	items: T[];
	keyOf: (item: T, index: number) => string;
	renderItem: (item: T, index: number, size: { width: number; height: number }) => React.ReactNode;
	/** Card width over height - the mobile card's own proportions. */
	cardAspect: number;
	loading?: boolean;
	emptyLabel: string;
	hintLabel?: string;
	/** The card the coverflow opens on. */
	initialIndex?: number;
	onActiveIndexChange?: (index: number) => void;
	/** Something to stand in the bottom row instead of the recent-tables / jackpot pair. */
	footer?: React.ReactNode;
	/** Design height of a custom footer; the cards give up whatever it takes past the 82. */
	footerHeight?: number;
};

export function DesktopStripPage<T>({
	items,
	keyOf,
	renderItem,
	cardAspect,
	loading = false,
	emptyLabel,
	hintLabel,
	initialIndex,
	onActiveIndexChange,
	footer,
	footerHeight = FOOTER_HEIGHT,
}: Props<T>) {
	const { navigate } = useNavigation<Nav>();
	const isLogged = useSelector(selectIsLogged);
	const { s, scale, side, contentWidth, contentHeight } = useDesktopLayout();
	const blockTop = Math.max(0, (contentHeight - s(BLOCK_HEIGHT)) / 2);
	const footerTop = BLOCK_HEIGHT - footerHeight;
	const navHeight = items.length > 1 ? getDesktopCoverflowNavHeight(1, Boolean(hintLabel)) : 0;
	const cardDesignHeight = Math.min(DESKTOP_STRIP_CARD_HEIGHT, footerTop - FOOTER_GAP - navHeight);
	const cardHeight = s(cardDesignHeight);
	const cardWidth = cardHeight * cardAspect;
	const size = { width: cardWidth, height: cardHeight };
	// Whatever the cards leave over above the bottom row is split above and below them.
	const stripTop = Math.max(0, (footerTop - FOOTER_GAP - navHeight - cardDesignHeight) / 2);

	return (
		<View pointerEvents='box-none' style={[styles.root, { paddingHorizontal: side }]}>
			<View pointerEvents='box-none' style={{ marginTop: blockTop, height: s(BLOCK_HEIGHT) }}>
				<View style={{ marginTop: s(stripTop), minHeight: cardHeight }}>
					{loading ? (
						<View style={[styles.centred, { height: cardHeight }]}>
							<ActivityIndicator size='large' color='#B7C1D1' />
						</View>
					) : items.length === 0 ? (
						<View style={[styles.centred, { height: cardHeight }]}>
							<Text style={[styles.empty, { fontSize: s(20) }]}>{emptyLabel}</Text>
						</View>
					) : (
						<DesktopCoverflow
							items={items}
							keyOf={keyOf}
							width={contentWidth}
							cardWidth={cardWidth}
							cardHeight={cardHeight}
							scale={scale}
							initialIndex={initialIndex}
							onActiveIndexChange={onActiveIndexChange}
							hintLabel={hintLabel}
							renderItem={(item, index) => renderItem(item, index, size)}
						/>
					)}
				</View>

				<View
					pointerEvents='box-none'
					style={[styles.bars, { top: s(footerTop), height: s(footerHeight) }]}
				>
					{footer ?? (
						<DesktopBottomBars
							contentWidth={contentWidth}
							scale={scale}
							onRecentTablesPress={() => navigate(isLogged ? 'MyTables' : 'Login')}
						/>
					)}
				</View>
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	root: {
		...StyleSheet.absoluteFillObject,
	},
	centred: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	empty: {
		color: '#FFFFFF',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
	},
	bars: {
		position: 'absolute',
		left: 0,
		right: 0,
	},
});

export default DesktopStripPage;
